/**
 * Touch-device detection. Everything mobile-specific (layout, controls, camera)
 * keys off this so the desktop experience is untouched.
 *
 * `?touch=1` / `?touch=0` in the URL forces the touch UI on or off.
 */

export type TouchPreference = 'on' | 'off' | 'auto';

/** Pure: reads a `touch` override out of a URL query string. */
export function resolveTouchPreference(search: string): TouchPreference {
  const v = new URLSearchParams(search).get('touch');
  if (v === '1' || v === 'on' || v === 'true') return 'on';
  if (v === '0' || v === 'off' || v === 'false') return 'off';
  return 'auto';
}

type Listener = (touch: boolean) => void;

const listeners = new Set<Listener>();
let touch = false;

function setTouch(value: boolean): void {
  if (value === touch) return;
  touch = value;
  document.documentElement.classList.toggle('is-touch', value);
  if (value) attachManifest();
  for (const fn of listeners) fn(value);
}

/** Makes the game installable ("Add to Home Screen") on phones and tablets only. */
function attachManifest(): void {
  if (document.querySelector('link[rel="manifest"]')) return;
  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = `${import.meta.env.BASE_URL}manifest.webmanifest`;
  document.head.append(link);
}

/** Call once at startup, before the UI is built. */
export function initDevice(): void {
  const pref = resolveTouchPreference(window.location.search);
  if (pref === 'on') return setTouch(true);
  if (pref === 'off') return;
  // Phones and tablets report a coarse primary pointer; touchscreen laptops don't
  // until they're actually touched.
  if (window.matchMedia('(pointer: coarse)').matches) setTouch(true);
  window.addEventListener('touchstart', () => setTouch(true), { once: true, passive: true });
}

export function isTouch(): boolean {
  return touch;
}

/** What the player is playing with, for instructions ("Space", "tap", "A"). */
export type Controls = 'keys' | 'touch' | 'pad';

export function onTouchChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True when the page can go fullscreen (Android Chrome yes, iPhone Safari no). */
export function canFullscreen(): boolean {
  return (
    typeof document.documentElement.requestFullscreen === 'function' && document.fullscreenEnabled === true
  );
}

export function toggleFullscreen(): void {
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  else void document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
}

/** Portrait phone/tablet (taller than wide). */
export function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth;
}
